import styled from "@emotion/styled";
import { getVar } from "../../theme/ui-variables/ui-variables";
import { Box, Button } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { warrantService } from "../../api/services/warrant.service";
import { useNavigate } from 'react-router-dom';

function TermsConditions() {
    const containerRef = useRef<HTMLDivElement>(null);
    const [hasReachedBottom, setHasReachedBottom] = useState(false);
    const { logout } = useAuth0();
    const navigate = useNavigate();

    const handleAgreement = async () => {
        await warrantService.currentUserHasAcceptedTerms();
        navigate('/');
    }

    const handleLogout = () => {
      logout({
        logoutParams: {
          client_id: process.env.REACT_APP_AUTH0_CLIENT_ID,
          returnTo: process.env.REACT_APP_AUTH0_LOGOUT_URL,
        },
      });
    };

    const checkScroll = () => {
        if (!containerRef.current) return;
    
        const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
        const threshold = 5;
        const atBottom = scrollHeight - (scrollTop + clientHeight) <= threshold;
        
        if (atBottom && !hasReachedBottom) {
          setHasReachedBottom(true);
        }
    };
    
    useEffect(() => {
        const container = containerRef.current;
        if (!container || hasReachedBottom) return;
    
        container.addEventListener('scroll', checkScroll);
        checkScroll(); 

        return () => container.removeEventListener('scroll', checkScroll);
      }, [hasReachedBottom]); 

    return (<MainWrapper>
        <SubMainWrapper ref={containerRef}>
            <Content>
                <Title>Buyer Pro Terms & Conditions</Title>
                <List>
                    <ListItem><ListLink href="#subtitle1"><ListItemText>Service Description</ListItemText></ListLink></ListItem>
                    <ListItem><ListLink href="#subtitle2"><ListItemText>Territory</ListItemText></ListLink></ListItem>
                    <ListItem><ListLink href="#subtitle3"><ListItemText>Authorized Legal Entities</ListItemText></ListLink></ListItem>
                    <ListItem><ListLink href="#subtitle4"><ListItemText>Buyer Seats</ListItemText></ListLink></ListItem>
                    <ListItem><ListLink href="#subtitle5"><ListItemText>Buyer Pro Users</ListItemText></ListLink></ListItem>
                    <ListItem><ListLink href="#subtitle6"><ListItemText>Pricing Conditions</ListItemText></ListLink></ListItem>
                    <ListItem><ListLink href="#subtitle7"><ListItemText>Feedback Provision</ListItemText></ListLink></ListItem>
                    <ListItem><ListLink href="#subtitle8"><ListItemText>Term and Termination</ListItemText></ListLink></ListItem>
                    <ListItem><ListLink href="#subtitle9"><ListItemText>Intellectual Property</ListItemText></ListLink></ListItem>
                    <ListItem><ListLink href="#subtitle10"><ListItemText>Confidentiality</ListItemText></ListLink></ListItem>
                    <ListItem><ListLink href="#subtitle11"><ListItemText>Prohibited Use of Buyer Pro</ListItemText></ListLink></ListItem>
                    <ListItem><ListLink href="#subtitle12"><ListItemText>Authorization and Acceptance</ListItemText></ListLink></ListItem>
                </List>
                <SubTitle id="subtitle1">1. SERVICE DESCRIPTION</SubTitle>
                <Paragraph> C Wire Buyer Pro is a SaaS platform that allows Partners to buy advertising inventory without the use of cookies. It enables the Partner to connect to a Demand Side Platform (DSP) and place online advertisements using targeting provided by C Wire. Billing for media buying will occur through the DSP and is not handled by C Wire.</Paragraph>
                <SubTitle id="subtitle2">2. TERRITORY</SubTitle>
                <Paragraph>This Agreement applies to Partners operating Buyer Pro within the territory corresponding to the legal entity for which the user works. While the specific legal entity may not be known at the time of registration, access to the platform is limited to users and teams associated with the legal entities later identified and confirmed by the Partner. Outsourced teams or contractors working on behalf of these legal entities are also included under this Agreement, provided they operate for the territory relevant to the legal entity as determined post-registration.</Paragraph>
                <SubTitle id="subtitle3">3. Authorized Legal Entities</SubTitle>
                <Paragraph>The services under this Agreement will be provided to the legal entities associated with the Partner, which will be confirmed post-registration. The Partner may provide the details of these legal entities at any time during the term of the Agreement.</Paragraph>
                <SubTitle id="subtitle4">4. Buyer Seats</SubTitle>
                <Paragraph>Buyer Pro services will be available to the Buyer Seats (DSP) designated by the Partner post-registration. The Partner may request to modify this list by written communication (email is sufficient).</Paragraph>
                <Paragraph>To be provided by the Partner post-registration:</Paragraph>
                <UnorderedList>
                    <UnorderedListItem>DSP</UnorderedListItem>
                    <UnorderedListItem>Buyer Seat Name</UnorderedListItem>
                    <UnorderedListItem>Buyer Seat ID</UnorderedListItem>
                </UnorderedList>
                <Paragraph>C Wire uses the Xandr Monetize SSP. All information provided by the partner must be suitable for this SSP.</Paragraph>
                <SubTitle id="subtitle5">5. Buyer Pro Users</SubTitle>
                <Paragraph>The initial users authorized to access Buyer Pro will be those designated by the Partner during or after the registration process. The Partner may update this list upon request via email. The Partner is required to inform C Wire if any user ceases employment or no longer works for the Partner’s designated legal entities to ensure the user’s access is revoked.
                </Paragraph>
                <SubTitle id="subtitle6">6. Pricing Conditions</SubTitle>
                <Paragraph>For an initial period of one year, Buyer Pro will be provided free of charge in exchange for feedback as described in section 7. Unless a new agreement is signed, this service will remain free of charge. Upcoming new features might come at different pricing conditions and might be excluded from this agreement.
                </Paragraph>
                <SubTitle id="subtitle7">7. Feedback Provision</SubTitle>
                <Paragraph>The Partner agrees to provide C Wire with constructive feedback on the service, including bug reports, suggestions for improvements, and performance-related information.
                </Paragraph>
                <SubTitle id="subtitle8">8. Term and Termination</SubTitle>
                <Paragraph>This Agreement is effective from the date of acceptance and will continue until terminated. Either party may terminate the agreement with a three-month written notice (email is sufficient).
                </Paragraph>
                <SubTitle id="subtitle9">9. Intellectual Property</SubTitle>
                <Paragraph>C Wire retains ownership of all intellectual property related to the Buyer Pro platform. The Partner is granted a non-exclusive, non-transferable, and revocable license to access and use Buyer Pro during the term of this Agreement. The Partner grants C Wire a worldwide, royalty-free license to use the Partner’s trademarks and logos for the promotion of Buyer Pro and provision of services, which will apply once the legal entity is confirmed post-registration.</Paragraph>
                <SubTitle id="subtitle10">10. Confidentiality</SubTitle>
                <Paragraph>The parties agree to keep all Confidential Information exchanged under this Agreement confidential. Confidential Information includes non-public information and trade secrets that are designated as confidential. This obligation remains in force for two years after termination, except for trade secrets, which must be kept confidential indefinitely.</Paragraph>
                <Paragraph>Each party may share Confidential Information with its employees or affiliates who need to know the information in order to fulfill obligations under the Agreement. The parties are also prohibited from issuing public statements about this Agreement without mutual consent.</Paragraph>
                <SubTitle id="subtitle11">11. Prohibited Use of Buyer Pro</SubTitle>
                <Paragraph>Unless expressly authorized, the Partner is prohibited from:</Paragraph>
                <UnorderedList>
                    <UnorderedListItem>
                        Selling, licensing, or sublicensing Buyer Pro.
                    </UnorderedListItem>
                    <UnorderedListItem>
                        Modifying, reverse engineering, or creating derivative works from Buyer Pro.
                    </UnorderedListItem>
                    <UnorderedListItem>
                        Using Buyer Pro to develop competing products or services.
                    </UnorderedListItem>
                    <UnorderedListItem>
                        Employing data mining or screen scraping tools on Buyer Pro.
                    </UnorderedListItem>
                </UnorderedList>
                <SubTitle id="subtitle13">12. Authorization and Acceptance </SubTitle>
                <Paragraph>By accepting this terms or by using the C Wire Buyer Pro platform, the User agrees to provide any necessary details of their legal entity post-registration and agrees to be personally bound by the terms of this Agreement.</Paragraph>
                <Paragraph>This adaptation allows for flexibility in the registration process, accounting for when the legal entity might be identified or confirmed after the user begins using the service, while still establishing a clear framework for access and usage based on the Partner’s organization.</Paragraph>
            </Content>
        </SubMainWrapper>
        <NavBar>
            <Button
            disabled={!hasReachedBottom}
                size="medium"
                className="tertiary-button"
                onClick={handleLogout}
            >
                Decline
            </Button>
            <Box sx={{ flex: '1 1 auto' }} />

            <Button
            disabled={!hasReachedBottom}
            
            size="medium" variant="contained" onClick={handleAgreement}>
                AGREE & PROCEED
            </Button>
        </NavBar>
    </MainWrapper>);
}

export default TermsConditions;

const MainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: hidden
`;
const SubMainWrapper = styled.div`
  overflow-y: auto;
`;
const Content = styled.div`
  margin: auto 180px;
`;
const List = styled.ol`
  margin: 30px 0 50px 0;
`;
const ListItem = styled.li`
  margin: 5px 0;
  font-size: 18px;
  font-weight: 600;
`;
const Title = styled.h1`
  margin: 100px 0 50px 0 ;
`;
const SubTitle = styled.h2`
  margin: 25px 0 ;
`;
const Paragraph = styled.p`
  margin: 25px 0;
`;
const UnorderedList = styled.ul`
  margin: 20px 0;
`;
const UnorderedListItem = styled.li`
  margin: 5px 0;
`;
const NavBar = styled.footer`
  width: 100%;
  height: ${getVar('headerHeight')};
  padding: 16px;
  display: flex;
  background-color: #fff;
  color: inherit;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.5);
`;
const ListLink = styled.a`
  color: inherit; 
  text-decoration: none;
`;
const ListItemText = styled.span`
  font-size: inherit;
  font-weight: inherit;
`;
